import type { SpeciesActivityTag } from '@/constants/speciesActivityTags';

export type SpeciesCategoryKey =
  | 'mushroom'
  | 'fruit'
  | 'green'
  | 'nut'
  | 'root'
  | 'wildflower'
  | 'fish'
  | 'game'
  | 'other';

type UrlCategorySegment =
  | 'mushrooms'
  | 'fruits'
  | 'greens'
  | 'nuts'
  | 'roots'
  | 'wildflowers'
  | 'fish'
  | 'game'
  | 'other';

const keyToUrlSegmentMap: Record<SpeciesCategoryKey, UrlCategorySegment> = {
  mushroom: 'mushrooms',
  fruit: 'fruits',
  green: 'greens',
  nut: 'nuts',
  root: 'roots',
  wildflower: 'wildflowers',
  fish: 'fish',
  game: 'game',
  other: 'other',
};

const urlSegmentToKeyMap: Record<UrlCategorySegment, SpeciesCategoryKey> = {
  mushrooms: 'mushroom',
  fruits: 'fruit',
  greens: 'green',
  nuts: 'nut',
  roots: 'root',
  wildflowers: 'wildflower',
  fish: 'fish',
  game: 'game',
  other: 'other',
};

const categoryLabelMap: Record<SpeciesCategoryKey, string> = {
  mushroom: 'Mushrooms',
  fruit: 'Fruits',
  green: 'Greens',
  nut: 'Nuts',
  root: 'Roots',
  wildflower: 'Wildflowers',
  fish: 'Fish',
  game: 'Game',
  other: 'Other',
};


/** Consolidated type filters for the public field guide listing. */
export type SpeciesListTypeFilterKey =
  | 'all'
  | 'mushroom'
  | 'fruits_nuts'
  | 'green'
  | 'root'
  | 'wildflower'
  | 'wild_harvest'
  | 'other';

type ListTypeFilterConfig = {
  label: string;
  categories: SpeciesCategoryKey[];
  urlSegment: string;
};

const listTypeFilterConfig: Record<
  Exclude<SpeciesListTypeFilterKey, 'all'>,
  ListTypeFilterConfig
> = {
  mushroom: { label: 'Mushrooms', categories: ['mushroom'], urlSegment: 'mushrooms' },
  fruits_nuts: {
    label: 'Fruits & Nuts',
    categories: ['fruit', 'nut'],
    urlSegment: 'fruits-nuts',
  },
  green: { label: 'Greens', categories: ['green'], urlSegment: 'greens' },
  root: { label: 'Roots', categories: ['root'], urlSegment: 'roots' },
  wildflower: {
    label: 'Wildflowers',
    categories: ['wildflower'],
    urlSegment: 'wildflowers',
  },
  wild_harvest: {
    label: 'Wild Harvest',
    categories: ['fish', 'game'],
    urlSegment: 'wild-harvest',
  },
  other: { label: 'Other', categories: ['other'], urlSegment: 'other' },
};

const legacyUrlSegmentToListTypeFilter: Record<string, SpeciesListTypeFilterKey> = {
  mushrooms: 'mushroom',
  'fruits-nuts': 'fruits_nuts',
  fruits: 'fruits_nuts',
  nuts: 'fruits_nuts',
  greens: 'green',
  roots: 'root',
  wildflowers: 'wildflower',
  'wild-harvest': 'wild_harvest',
  fish: 'wild_harvest',
  game: 'wild_harvest',
  other: 'other',
};

export const SPECIES_LIST_TYPE_FILTER_KEYS: Exclude<
  SpeciesListTypeFilterKey,
  'all'
>[] = [
  'mushroom',
  'fruits_nuts',
  'green',
  'root',
  'wildflower',
  'wild_harvest',
  'other',
];

export function getListTypeFilterFromUrl(
  segment?: string | null,
): SpeciesListTypeFilterKey {
  if (!segment) return 'all';
  return legacyUrlSegmentToListTypeFilter[segment.toLowerCase()] ?? 'all';
}

export function getListTypeFilterUrlSegment(
  key: SpeciesListTypeFilterKey,
): string | undefined {
  if (key === 'all') return undefined;
  return listTypeFilterConfig[key].urlSegment;
}

export function getListTypeFilterLabel(key: SpeciesListTypeFilterKey): string {
  if (key === 'all') return 'All';
  return listTypeFilterConfig[key].label;
}

export function speciesMatchesListTypeFilter(
  category: string | undefined | null,
  filterKey: SpeciesListTypeFilterKey,
): boolean {
  if (filterKey === 'all') return true;
  const normalized = String(category || '').toLowerCase() as SpeciesCategoryKey;
  return listTypeFilterConfig[filterKey].categories.includes(normalized);
}

/** Type tabs shown for each activity filter on the field guide hub. */
export function getListTypeFiltersForActivity(
  activity: 'all' | SpeciesActivityTag,
): SpeciesListTypeFilterKey[] {
  switch (activity) {
    case 'foraging':
      return [
        'all',
        'mushroom',
        'fruits_nuts',
        'green',
        'root',
        'other',
      ];
    case 'wildflower':
      return ['all', 'wildflower'];
    case 'fishing':
    case 'hunting':
      return ['all', 'wild_harvest'];
    case 'all':
      return [
        'all',
        'mushroom',
        'fruits_nuts',
        'green',
        'root',
        'wildflower',
        'other',
      ];
    default:
      return ['all', ...SPECIES_LIST_TYPE_FILTER_KEYS];
  }
}

export function getCategoryKeyFromUrl(segment?: string | null): SpeciesCategoryKey | 'all' {
  if (!segment) return 'all';
  const normalized = segment.toLowerCase() as UrlCategorySegment;
  return urlSegmentToKeyMap[normalized] ?? 'all';
}

export function getCategoryUrlFromKey(key: SpeciesCategoryKey | 'all'): string {
  if (key === 'all') return 'all';
  return keyToUrlSegmentMap[key];
}

export function getCategoryLabel(key: SpeciesCategoryKey | 'all'): string {
  if (key === 'all') return 'All';
  return categoryLabelMap[key];
}
